import DividerLine from "components/common/divider/line";
import { Forward } from "components/common/Icons";
import List from "components/common/List/list";
import { StrategySkeletonMobile } from "components/skeleton/main";
import { TrackedLink } from "lib/analytics/react/link";
import { TrackedList, TrackedListItem } from "lib/analytics/react/list";
import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { TYPO } from "styles/Typo";
import { useHotTopicList } from "utils/clientAPIs/topic";
import { KOREA } from "utils/constant/ITME_INFO";
import { routeToStockDetailPage } from "utils/routing";
import useDesktopSlider from "utils/useDesktopSlider";
import { ERROR_LOGO_URL, getPublicAssetUrl } from "utils/objectStorage";

const Card = ({ content }: { content: any }) => {
  const router = useRouter();
  const { t } = useTranslation("main");
  return (
    <TrackedLink
      href={`/topic?tpid=${content.id}`}
      asTrace={true}
      itemspec={{
        kind: "topic",
        topic_id: content.id,
        rank: content.rank,
      }}
      fromListId="explore_hot_topic_cardlist"
      data-gtm-id={`explore-hot-topic-card`}
    >
      <div className=" bg-white rounded-[30px] w-[295px]  shrink-0 overflow-hidden flex flex-col gap-2">
        <div className="flex flex-col px-6 pt-6 gap-1">
          <span className={`${TYPO.TYPO5_SB} text-gray70`}>
            {content.title}
          </span>
        </div>
        <TrackedList
          listId="explore_hot_topic_cardlist"
          totalCount={content?.assets?.length}
        >
          <div className="grow py-2 flex flex-col gap-2 ">
            {content?.assets?.map((asset: any, i: number) => (
              <TrackedListItem
                itemData={{
                  kind: "stock",
                  rank: i + 1,
                  ticker: asset.ITEM_CD_DL,
                  market: asset?.LOC === KOREA ? "kr" : "us",
                }}
                className="cursor-pointer"
                key={i}
                data-gtm-id={`explore-hot-topic-card-asset`}
                onClick={(event: any) => {
                  event.stopPropagation(); // 부모로 이벤트 전파 방지
                  window.dataLayer.push({
                    event: "cMainStratList",
                  });
                  routeToStockDetailPage(router, asset.ITEM_CD_DL);
                }}
              >
                <List
                  HR_ITEM_NM={""}
                  item={
                    router.locale === "ko"
                      ? asset?.ITEM_KR_NM
                      : asset?.ITEM_ENG_NM
                  }
                  src={
                    getPublicAssetUrl(`logos/${asset.ITEM_CD_DL}.png`) ||
                    ERROR_LOGO_URL
                  }
                />
              </TrackedListItem>
            ))}
          </div>
        </TrackedList>

        <div
          className={`w-full  flex flex-col `}
          // onClick={(event: any) => {
          //   event.stopPropagation(); // 부모로 이벤트 전파 방지
          // }}
        >
          <DividerLine size="sm" />
          <div
            className={`cursor-pointer px-[18px] py-3.5 w-full text-center flex justify-center items-center gap-1`}
          >
            <span className={`${TYPO.TYPO6_MD} text-gray60`}>
              {t("viewMore")}
            </span>
          </div>
        </div>
      </div>
    </TrackedLink>
  );
};

function HotTopicList({
  isMobile,
  initialList,
}: {
  isMobile: boolean;
  initialList?: any[] | null;
}) {
  const [data, isValid] = useHotTopicList({
    limit: 3,
    fallbackData: initialList ?? undefined,
  });
  const router = useRouter();
  const { t } = useTranslation("main");

  const horizontalContents = data
    ? data
        ?.map((item: any) => {
          return {
            ...item,
            assets: item?.assets?.slice(0, 3),
            onclick: () => {
              router.push(`/topic?tpid=${item?.id}`);
            },
          };
        })
        ?.filter(
          (item: any) => item?.assets?.length > 0 && item.layout_type === "CARD"
        )
    : [];

  const verticalContents = data
    ? data
        ?.map((item: any, i: number) => {
          return {
            ...item,
            rank: i + 1,
            assets: item?.assets?.slice(0, 3),
            onclick: () => {
              router.push(`/topic?tpid=${item?.id}`);
            },
          };
        })
        ?.filter(
          (item: any) => item?.assets?.length > 0 && item.layout_type === "LIST"
        )
    : [];

  const { Slider, sliderProps } = useDesktopSlider(horizontalContents);

  if (isValid || (!isValid && !data?.length))
    return (
      <div className="bg-gray2">
        <StrategySkeletonMobile />
      </div>
    );

  return (
    <div className=" bg-gray2">
      <div className="pt-[30px] px-6">
        <span className={`${TYPO.TYPO4_SB} text-gray80`}>
          {t("hotTopics")}
        </span>
      </div>
      {isMobile ? (
        <div className="overflow-x-auto scroll">
          <div className="flex  gap-4 p-6 w-fit items-stretch">
            {horizontalContents?.map((content: any, i: number) => (
              <Card content={content} key={i} />
            ))}
          </div>
        </div>
      ) : (
        <Slider {...sliderProps}>
          {horizontalContents.map((content: any, i: number) => (
            <Card content={content} key={i} />
          ))}
        </Slider>
      )}
      <div>
        {verticalContents.map((content: any, i: number) => {
          return (
            <TrackedLink
              href={`/topic?tpid=${content.id}`}
              asTrace={true}
              itemspec={{
                kind: "topic",
                topic_id: content.id,
                rank: i + 1,
              }}
              fromListId="explore_hot_topic_list"
              key={i}
              data-gtm-id={`explore-hot-topic-list`}
            >
              <div className="py-2 ">
                <div className="w-full px-6 py-2 gap-3 items-center flex cursor-pointer">
                  {content?.img ? (
                    <div className="bg-white rounded-[16px]">
                      <div className="w-[80px] h-[60px] rounded-[16px] overflow-hidden bg-gray5 flex items-center justify-center">
                        <Image
                          unoptimized
                          height={80}
                          width={60}
                          alt="topic-thumnail"
                          src={content?.img}
                          key={`image-${content?.img}`}
                          priority={false}
                          className="object-cover w-full h-full"
                        />
                      </div>
                    </div>
                  ) : null}

                  <div className="flex flex-col grow">
                    <span className={`${TYPO.TYPO5_1_SB} text-gray70`}>
                      {content.title}
                    </span>
                    <span className={`${TYPO.TYPO8_RG} text-gray50 break-keep`}>
                      {content.description}
                    </span>
                  </div>
                  <Forward width={16} height={16} fill={"gray30"} />
                </div>
              </div>
            </TrackedLink>
          );
        })}
      </div>
    </div>
  );
}

export default HotTopicList;
